'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import areasData from '../../data/suburb-list.json'
import siteConfig from '../../data/site-config.json'
import { trackPhoneClick } from '../../lib/analytics'
import AvailabilityBadge from '../common/AvailabilityBadge'

const REGION_CONFIG = {
    Central: { color: '#D4AF37', label: 'Central', x: 300, y: 280, labelX: 300, labelY: 280 },
    North: { color: '#2DD4A8', label: 'North', x: 300, y: 120, labelX: 300, labelY: 130 },
    South: { color: '#4A90D9', label: 'South', x: 300, y: 440, labelX: 300, labelY: 430 },
    East: { color: '#E8A838', label: 'East', x: 460, y: 280, labelX: 460, labelY: 280 },
    West: { color: '#A86EDB', label: 'West', x: 140, y: 280, labelX: 140, labelY: 280 },
}

// Simplified SVG paths for Perth metro region zones
const REGION_PATHS = {
    Central: 'M250,240 L350,240 L350,320 L250,320 Z',
    North: 'M200,40 L400,40 L420,100 L380,160 L350,240 L250,240 L220,160 L180,100 Z',
    South: 'M220,400 L250,320 L350,320 L380,400 L400,480 L370,530 L230,530 L200,480 Z',
    East: 'M350,240 L380,160 L420,100 L500,120 L530,200 L530,360 L500,440 L380,400 L350,320 Z',
    West: 'M250,240 L220,160 L180,100 L100,120 L70,200 L70,360 L100,440 L220,400 L250,320 Z',
}

export default function ServiceAreas() {
    const [activeRegion, setActiveRegion] = useState('North')
    const [hoveredRegion, setHoveredRegion] = useState(null)
    const [isMounted, setIsMounted] = useState(false)

    useEffect(() => {
        setIsMounted(true)
    }, [])
    const regions = Object.keys(areasData.regions)

    return (
        <section className="service-areas-section" style={{
            padding: 'var(--spacing-lg) 0',
            background: 'linear-gradient(160deg, #14221B 0%, #23322B 45%, #14221B 100%)',
            overflow: 'hidden'
        }}>
            <div className="container">
                {/* Header */}
                <div className="section-head" style={{ marginBottom: 'var(--spacing-md)' }}>
                    <span className="section-eyebrow on-dark">Service Areas</span>
                    <h2 style={{ color: 'var(--white)' }}>
                        Car Removal Across All Perth Suburbs
                    </h2>
                    <p style={{ color: 'rgba(255,255,255,0.72)' }}>
                        We provide free car removal and pay instant cash for unwanted vehicles throughout the Perth metropolitan area.
                        Same-day pickup available in most locations.
                    </p>
                </div>

                {/* Map + Stats Row */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 'var(--spacing-md)',
                    alignItems: 'center',
                    marginBottom: 'var(--spacing-md)'
                }} className="areas-map-grid">
                    {/* SVG Map */}
                    <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <svg viewBox="20 0 560 570" style={{ width: '100%', maxWidth: '500px', filter: 'drop-shadow(0 0 40px rgba(45, 212, 168, 0.15))' }}>
                            <defs>
                                {/* Glow filter */}
                                <filter id="glow">
                                    <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                                    <feMerge>
                                        <feMergeNode in="coloredBlur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                                <filter id="glowStrong">
                                    <feGaussianBlur stdDeviation="8" result="coloredBlur" />
                                    <feMerge>
                                        <feMergeNode in="coloredBlur" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                            </defs>

                            {/* Region shapes */}
                            {regions.map(region => {
                                const cfg = REGION_CONFIG[region]
                                const isActive = activeRegion === region
                                const isHovered = hoveredRegion === region
                                return (
                                    <g key={region}
                                        onClick={() => setActiveRegion(region)}
                                        onMouseEnter={() => setHoveredRegion(region)}
                                        onMouseLeave={() => setHoveredRegion(null)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {/* Region path */}
                                        <path
                                            d={REGION_PATHS[region]}
                                            fill={isActive ? cfg.color : `${cfg.color}33`}
                                            stroke={cfg.color}
                                            strokeWidth={isActive || isHovered ? 2.5 : 1}
                                            opacity={isActive ? 0.85 : isHovered ? 0.65 : 0.4}
                                            style={{
                                                transition: 'all 0.4s ease',
                                                filter: isActive || isHovered ? 'url(#glow)' : 'none'
                                            }}
                                        />
                                        {/* Region label */}
                                        <text
                                            x={cfg.labelX}
                                            y={cfg.labelY - 10}
                                            textAnchor="middle"
                                            fill={isActive ? '#fff' : 'rgba(255,255,255,0.6)'}
                                            fontSize={isActive ? '16' : '14'}
                                            fontWeight={isActive ? '700' : '500'}
                                            fontFamily="'Inter', system-ui, sans-serif"
                                            style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}
                                        >
                                            {cfg.label}
                                        </text>
                                        {/* Suburb count badge */}
                                        <text
                                            x={cfg.labelX}
                                            y={cfg.labelY + 12}
                                            textAnchor="middle"
                                            fill={isActive ? cfg.color : 'rgba(255,255,255,0.4)'}
                                            fontSize="11"
                                            fontFamily="'Inter', system-ui, sans-serif"
                                            fontWeight="600"
                                            style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}
                                        >
                                            {areasData.regions[region].length} suburbs
                                        </text>
                                    </g>
                                )
                            })}

                            {/* Pulsing circles from center (business location) */}
                            <circle className="pulse-ring pulse-ring-1" cx="300" cy="285" r="60" fill="none" stroke="#D4AF37" strokeWidth="1.5" />
                            <circle className="pulse-ring pulse-ring-2" cx="300" cy="285" r="60" fill="none" stroke="#2DD4A8" strokeWidth="1.5" />
                            <circle className="pulse-ring pulse-ring-3" cx="300" cy="285" r="60" fill="none" stroke="#D4AF37" strokeWidth="1" />

                            {/* Center dot (business location) */}
                            <circle cx="300" cy="285" r="6" fill="#D4AF37" filter="url(#glowStrong)" />
                            <circle cx="300" cy="285" r="3" fill="#fff" />
                        </svg>
                    </div>

                    {/* Stats + Info Panel */}
                    <div style={{ color: '#fff' }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '1rem',
                            marginBottom: 'var(--spacing-md)'
                        }}>
                            {[
                                { value: '400+', label: 'Suburbs Covered' },
                                { value: '5', label: 'Perth Regions' },
                                { value: 'Same Day', label: 'Pickup Available' },
                                { value: 'FREE', label: 'Towing Service' },
                            ].map((stat, i) => (
                                <div key={i} style={{
                                    background: 'rgba(255,255,255,0.05)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '12px',
                                    padding: '1.2rem 1rem',
                                    textAlign: 'center',
                                    backdropFilter: 'blur(10px)',
                                    transition: 'all 0.3s ease'
                                }}
                                    className="stat-card"
                                >
                                    <div style={{
                                        fontSize: '1.6rem',
                                        fontWeight: '800',
                                        background: 'linear-gradient(135deg, #D4AF37, #2DD4A8)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        marginBottom: '0.25rem'
                                    }}>
                                        {stat.value}
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', fontWeight: '500' }}>
                                        {stat.label}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p style={{
                            color: 'rgba(255,255,255,0.5)',
                            fontSize: '0.85rem',
                            lineHeight: '1.6',
                            fontStyle: 'italic'
                        }}>
                            Click on a region on the map to explore suburbs we service. Our team provides fast car removal across all of Perth.
                        </p>
                    </div>
                </div>

                {/* Region Tabs */}
                <div style={{ marginBottom: 'var(--spacing-sm)' }}>
                    <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        marginBottom: 'var(--spacing-sm)'
                    }}>
                        {regions.map(region => {
                            const cfg = REGION_CONFIG[region]
                            const isActive = activeRegion === region
                            return (
                                <button
                                    key={region}
                                    onClick={() => setActiveRegion(region)}
                                    style={{
                                        padding: '0.6rem 1.2rem',
                                        border: `1.5px solid ${isActive ? cfg.color : 'rgba(255,255,255,0.15)'}`,
                                        borderRadius: '50px',
                                        background: isActive ? `${cfg.color}20` : 'rgba(255,255,255,0.03)',
                                        color: isActive ? cfg.color : 'rgba(255,255,255,0.5)',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem',
                                        fontWeight: isActive ? '700' : '500',
                                        transition: 'all 0.3s ease',
                                        fontFamily: 'inherit',
                                        backdropFilter: 'blur(8px)',
                                        boxShadow: isActive ? `0 0 20px ${cfg.color}30` : 'none'
                                    }}
                                >
                                    {region} Perth
                                    <span style={{
                                        marginLeft: '0.4rem',
                                        fontSize: '0.75rem',
                                        opacity: 0.7
                                    }}>
                                        ({areasData.regions[region].length})
                                    </span>
                                </button>
                            )
                        })}
                    </div>

                    {/* Suburb Lists — all rendered in DOM for SEO, visibility toggled */}
                    {regions.map(region => (
                        <div
                            key={region}
                            style={{
                                display: activeRegion === region ? 'flex' : 'none',
                                flexWrap: 'wrap',
                                gap: '0.5rem',
                                justifyContent: 'center',
                                padding: '1rem',
                                background: 'rgba(255,255,255,0.03)',
                                borderRadius: '12px',
                                border: '1px solid rgba(255,255,255,0.06)'
                            }}
                            aria-label={`${region} Perth suburbs we service`}
                        >
                            {(activeRegion === region || !isMounted) && areasData.regions[region].map(suburb => {
                                    const name = typeof suburb === 'string' ? suburb : suburb.name;
                                    const slug = typeof suburb === 'string' ? suburb.toLowerCase().replace(/\s+/g, '-') : suburb.slug;
                                    return (
                                <Link
                                    key={name}
                                    href={`/areas/${slug}`}
                                    style={{
                                        display: 'inline-block',
                                        padding: '0.35rem 0.75rem',
                                        background: 'rgba(255,255,255,0.06)',
                                        borderRadius: '20px',
                                        fontSize: '0.82rem',
                                        color: 'rgba(255,255,255,0.65)',
                                        transition: 'all 0.25s ease',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        whiteSpace: 'nowrap'
                                    }}
                                    className="suburb-pill"
                                >
                                    {name}
                                </Link>
                                    );
                            })}
                        </div>
                    ))}
                </div>

                {/* CTA Banner */}
                <div style={{
                    textAlign: 'center',
                    marginTop: 'var(--spacing-sm)',
                    padding: 'var(--spacing-md)',
                    background: 'linear-gradient(135deg, rgba(10,92,54,0.9), rgba(18,126,75,0.9))',
                    borderRadius: '16px',
                    border: '1px solid rgba(212,175,55,0.3)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                }}>
                    <h3 style={{ color: '#fff', marginBottom: 'var(--spacing-xs)', fontSize: '1.3rem' }}>
                        Don&apos;t See Your Suburb?
                    </h3>
                    <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: 'var(--spacing-sm)' }}>
                        We service ALL Perth suburbs! Call us for a free quote regardless of your location.
                    </p>
                    <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'center' }}>
                        <AvailabilityBadge />
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Link
                            href="/quote"
                            className="btn btn-secondary"
                        >
                            Get Free Quote
                        </Link>
                        <a
                            href={`tel:${siteConfig.business.phone.replace(/\s/g, '')}`}
                            onClick={() => trackPhoneClick('service_areas_cta')}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.8rem 1.5rem',
                                border: '2px solid rgba(255,255,255,0.5)',
                                borderRadius: 'var(--border-radius)',
                                color: '#fff',
                                fontWeight: '600',
                                fontSize: '1rem',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            📞 {siteConfig.business.phone}
                        </a>
                    </div>
                </div>
            </div>
        </section>
    )
}
